import { AfterViewInit, Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BookingFlowService } from '../booking-flow/service/booking-flow.service';
import { ToastService } from '@shared/services/toast.service';
import { DialogControlService } from '@shared/services/dialog-control.service';
import { ModifyPaymentModalComponent } from '../modify-payment-popup-modal/modify-payment-popup-modal.component';
import { PaymentSucessComponent } from '../booking-flow/payment-success/payment-success.component';
import {
  UPDATED_USER_TYPE,
  USER_ROLES,
} from '@shared/constant/shared.constant';
import { autoUnsubWith, Utility } from '@shared/services/utility.abstract';
import { LuxonFormatPipe } from '@shared/custom-date-pipe';
import { DateTime } from 'luxon';
import { FilterService } from '@shared/services/filter.service';
import { EncryptionStorageService } from '@core/services/encryption-storage.service';

interface TimeOption {
  name: string;
  value: string;
}
interface SpotOption {
  name: string;
  id: string;
}
@Component({
  selector: 'app-modify-bookings-popup',
  templateUrl: './modify-bookings-popup.component.html',
  styleUrls: ['./modify-bookings-popup.component.scss'],
  providers: [LuxonFormatPipe]
})
export class ModifyBookingsPopupComponent implements AfterViewInit, OnInit{
  readonly destroyRef = inject(DestroyRef);
  @ViewChild('focusTrap') focusTrap!: ElementRef;

  bookingForm: FormGroup;
  selectedHour: number;
  timeOptions: TimeOption[] = [];
  bookingData: any;
  isShowSlotInpt: boolean = false;
  spotOptions: SpotOption[] = [];
  userRoles = USER_ROLES;
  assisstedListUrl: string;
  errorMessage: string;
  minDate: Date | null;
  depatureMinDate: Date | null;
  userType = UPDATED_USER_TYPE;

  constructor(
    private readonly fb: FormBuilder,
    private readonly ref: DynamicDialogRef,
    private readonly config: DynamicDialogConfig,
    private readonly bookingFlowService: BookingFlowService,
    private readonly toastService: ToastService,
    private readonly dialogControlService: DialogControlService,
    private readonly luxonFormatPipe: LuxonFormatPipe,
    private readonly filterService: FilterService,
    private readonly encryptStorage: EncryptionStorageService
  ) {}

  ngOnInit(): void {
    this.bookingData = this.config?.data?.bookingData;
    this.timeOptions = this.generateTimeOptions();
    const initialArrivalTime = this.luxonFormatPipe.transform(this.bookingData?.startDate, 'HH:00');
    this.bookingForm = this.fb.group({
      arrivalDate: [this.setDateWithSameTimeZone(this.bookingData?.startDate), Validators.required],
      arrivalTime: [initialArrivalTime, Validators.required],
      departureDate: [this.setDateWithSameTimeZone(this.bookingData?.endDate), Validators.required],
      spot: [this.bookingData?.slotId],
    });
    this.bookingForm.get('spot')?.disable();
    this.spotOptions = [
      {
        name: this.bookingData?.slotName,
        id: this.bookingData?.slotId,
      },
    ];
    if (this.bookingData?.bookingType === 2) {
      this.updateMinDate();
    }
  }

  setDateWithSameTimeZone(date): Date {
    const dt = DateTime.fromISO(date, { setZone: true });
    return new Date(dt.year, dt.month - 1, dt.day, dt.hour, dt.minute);
  }

  onDateChange(): void {
    this.bookingForm.controls['arrivalDate'].updateValueAndValidity(); // Revalidate on change
  }
  ngAfterViewInit() {
    setTimeout(() => {
      this.focusTrap.nativeElement.focus();
    }, 100);
  }
  generateTimeOptions(): TimeOption[] {
    const options: TimeOption[] = [];

    for (let hour = 0; hour < 24; hour++) {
      const hour12 = hour % 12 || 12;
      const period = hour < 12 ? 'AM' : 'PM';
      const timeValue = `${String(hour).padStart(2, '0')}:00`;
      const timeName = `${String(hour12).padStart(2, '0')}:00 ${period}`;
      options.push({ name: timeName, value: timeValue });
    }

    return options;
  }

  onInputChange(lable: string): void {
    if (this.bookingData?.bookingType === 0 && lable === 'arrivalDate') {
      const arrivalTime = this.bookingForm.get('arrivalTime')?.value;
      let selectedArrivalDate = new Date(
        this.bookingForm.get('arrivalDate')?.value
      );
      let currentDepartureDate = new Date(
        this.bookingForm.get('departureDate')?.value
      );
      let arrivalDateTime = this.formatDateTime(
        selectedArrivalDate,
        arrivalTime
      );
      let departureDateTime = this.formatDateTime(
        currentDepartureDate,
        arrivalTime
      );
      if (arrivalDateTime >= departureDateTime) {
        const newDepartureDate = new Date(selectedArrivalDate);
        newDepartureDate.setDate(newDepartureDate.getDate() + 1);
        this.bookingForm.get('departureDate').setValue(newDepartureDate);
      }
      this.depatureMinDate = new Date(selectedArrivalDate);
      this.depatureMinDate.setDate(this.depatureMinDate.getDate() + 1);
    }

    this.errorMessage = '';
    if (this.bookingData?.userType === this.userType.PARKER) {

      this.bookingForm.get('spot')?.enable();
      const arrivalDateString = this.bookingForm.get('arrivalDate')?.value;
      const arrivalTime = this.bookingForm.get('arrivalTime')?.value;
      const departureDateString = this.bookingForm.get('departureDate')?.value;

      const arrivalDate = new Date(arrivalDateString);
      const departureDate = new Date(departureDateString);
      const arrivalDateTime = this.formatDateTime(arrivalDate, arrivalTime);
      const departureDateTime = this.formatDateTime(
        departureDate,
        arrivalTime
      );

      const request = {
        arrivalDateTime,
        departureDateTime,
        bookingId: this.bookingData.id,
        parkingLotId: this.bookingData?.parkingLotId,
        bookingType: this.bookingData?.bookingType,
        vehicleType: this.bookingData?.vehicleTypeId,
      };

      this.bookingFlowService.getAvailableSlots(request)
      .pipe(autoUnsubWith(this.destroyRef))
      .subscribe({
        next: (response: any) => {

          if (response.availableSlots?.length > 0) {
            const slotMap = new Map(
              response.availableSlots.map((slot: any) => [
                slot.name,
                { name: slot.name, id: slot.id },
              ])
            );

            const defaultSlot: SpotOption =
              this.bookingData?.slotName &&
              slotMap.has(this.bookingData.slotName)
                ? {
                    name: this.bookingData.slotName,
                    id: this.bookingData.slotId,
                  }
                : (Array.from(slotMap.values())[0] as SpotOption);

            this.spotOptions = Array.from(slotMap.values()) as SpotOption[];
            this.bookingForm.patchValue({ spot: defaultSlot.id });

            this.isShowSlotInpt = true;
          }
        },
        error: (error: any) => {

          this.errorMessage = error.error.message;
        },
      });
    }
  }

  formatDateTime(date: Date, hour: number): string {
    const d = this.combineDateHours(date, hour);
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const seconds = d.getSeconds().toString().padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  combineDateHours(date, hours): Date {
    let newHour = parseInt(hours, 10);
    let updatedDate = new Date(date);
    updatedDate.setHours(newHour);
    updatedDate.setMinutes(0);
    updatedDate.setSeconds(0);
    updatedDate.setMilliseconds(0);
    return updatedDate;
  }

  Submit(): void {
    const arrivalDate = new Date(this.bookingForm.get('arrivalDate')?.value);
    const arrivalTime = this.bookingForm.get('arrivalTime')?.value;
    const departureDate = new Date(
      this.bookingForm.get('departureDate')?.value
    );

    const arrivalDateTime = this.formatDateTime(arrivalDate, arrivalTime);
    const departureDateTime =
      this.bookingData?.bookingType === 2
        ? null
        : this.formatDateTime(departureDate, arrivalTime);
    if (!this.errorMessage) {

      const request = {
        bookingId: this.bookingData.id,
        arrivalDateTime,
        departureDateTime,
        slotId:
          this.bookingData?.slotName === 'Any'
            ? null
            : this.bookingForm.get('spot')?.value,
      };

      this.bookingFlowService
        .checkBookingExtendDatesPayment(request)
        .pipe(autoUnsubWith(this.destroyRef))
        .subscribe({
          next: (response: any) => {

            this.errorMessage = '';
            if (response?.isPaymentRequired) {
              this.dialogControlService.openDialog(
                ModifyPaymentModalComponent,
                {
                  styleClass: 'pay-repay',
                  data: {
                    bookingId: this.bookingData.id,
                    invoiceId: null,
                    driverId: this.bookingData.createdBy,
                    bookingType: this.bookingData.bookingType,
                    data: response,
                    arrivalDateTime: arrivalDateTime,
                    departureDateTime: departureDateTime,
                    slotId: this.bookingForm.get('spot')?.value,
                    userType: this.bookingData.userType,
                  },
                }
              );
              this.close();
            } else {
              this.updatedBookingById(arrivalDateTime, departureDateTime);
            }
          },
          error: (error: any) => {

            this.errorMessage = error.error.message;
          },
        });
    }
  }

  updateMinDate(): void {
    if (this.bookingData.bookingType === 2) {
      let pastDate = new Date();
      pastDate.setDate(pastDate.getDate() - 25); // Allow up to 25 days in the past
      this.minDate = pastDate;
    } else {
      this.minDate = new Date(); // Set to today (no back-dating allowed)
    }
  }

  updatedBookingById(arrivalDateTime, departureDateTime) {
    this.filterService.preserveDataOnNextNavigation = true;

    let request = {
      bookingId: this.bookingData.id,
      arrivalDateTime,
      departureDateTime,
      slotId:
        this.bookingData?.slotName === 'Any'
          ? null
          : this.bookingForm.get('spot')?.value,
      paymentMethod: this.bookingData.paymentMethodType === 'Card' ? 1 : 2,
      isPreferredPaymentMethod: null,
      preferredPaymentMethodId: null,
      timezone: Utility.getLocalTimeZone(),
    };
    this.bookingFlowService.updateBookingById(request)
    .pipe(autoUnsubWith(this.destroyRef))
    .subscribe({
      next: (_response: any) => {
        this.assistedListUrl();
        this.close();
        this.dialogControlService.openDialog(PaymentSucessComponent, {
          width: '30%',
          data: {
            assistedUrl: this.assisstedListUrl,
            bodyText: 'Booking Updated Successfully',
            buttonText: 'OK',
            heading: 'Success',
            bookingId: this.bookingData.id,
          },
          arrivalDateTime: arrivalDateTime,
          departureDateTime: departureDateTime,
        });

      },
      error: (error: any) => {
        this.toastService.showToastMessage(
          'error',
          'Error',
          error.error.message
        );

      },
    });
  }

  assistedListUrl() {
    let roleName = this.encryptStorage.getItem('roleName');
    let bookingDetailsPath =
      this.bookingData.userType === this.userType.PARKER
        ? 'booking-details'
        : 'group-booking-details';

    switch (roleName) {
      case this.userRoles.SUPER_ADMIN:
        this.assisstedListUrl = `super-admin/${bookingDetailsPath}`;
        break;
      case this.userRoles.PARKING_MANAGER:
      case this.userRoles.PARKING_OWNER:
        this.assisstedListUrl = `manager/${bookingDetailsPath}`;
        break;
    }
  }

  close() {
    this.ref.close();
  }
}
